import { useIsSuperadmin } from '@/hooks/admin/useIsSuperadmin';
import { useCustomer } from '@/hooks/useCustomer';

/**
 * useActingAs — is the signed-in user a superadmin currently operating inside a
 * customer's context ("acting as")? Used by the loud Acting-as bar and by the
 * name-the-customer confirms on money writes (place order / take payment /
 * save card / autopay), so a staff member can never quietly file a real-money
 * action against a customer they forgot they were acting as.
 *
 * "Acting as" = superadmin + a customer context is active. Derived from
 * useCustomer, which resolves the PER-TAB acting context (docs/ACTING-CONTEXT.md)
 * — so the bar/confirms follow whatever customer this tab is in. (For a plain
 * customer there is no impersonation, so this is always false.)
 */
export function useActingAs() {
  const { isSuperadmin } = useIsSuperadmin();
  const { data: ctx } = useCustomer();
  const isActingAs = !!isSuperadmin && !!ctx?.customer;
  const customerName = ctx?.customer.name ?? null;

  /**
   * Gate a money write behind a name-the-customer confirm when acting as.
   * Returns true (proceed) immediately for a normal customer or a superadmin
   * not acting as anyone; otherwise pops a confirm that names the customer.
   * `action` reads as a sentence: confirmMoneyAction('Place this order')
   * → "You are acting as ACME. Place this order for this customer?"
   */
  function confirmMoneyAction(action: string): boolean {
    if (!isActingAs) return true;
    return window.confirm(
      `You are acting as ${customerName}.\n\n${action} for this customer?`,
    );
  }

  return {
    isActingAs,
    customerName,
    customerActive: ctx?.customer.active ?? null,
    confirmMoneyAction,
  };
}
